import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import toast, { Toaster } from 'react-hot-toast';
import {
  ClipboardList,
  Calendar,
  CheckCircle,
  Send,
  Loader2,
  FileText,
  AlertCircle,
  User,
  Users,
} from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { EosSurveyService } from '../../services/EosSurveyService';
import { EosSurveySubmission } from '../../@types/eosSurvey';
import {
  PageContainer,
  MainContent,
  Card,
  Heading1,
  Heading2,
  Text,
  Button,
  FormGroup,
  FormLabel,
  FlexColumn,
  Spacer,
} from '../../styles/StyledComponents';
import { ThemeColors, Spacing, Typography, BorderRadius, Shadows } from '../../styles/ThemeStyles';

/* ─────────────────── Styled Components ─────────────────── */

const SurveyPageContainer = styled.div`
  padding: ${Spacing.lg};
  max-width: 800px;
  margin: 80px auto 0 auto;
  width: 100%;

  @media (max-width: 768px) {
    padding: ${Spacing.md};
    margin-top: 70px;
  }
`;

const SurveyBanner = styled.div`
  background: linear-gradient(135deg, ${ThemeColors.primary.main} 0%, ${ThemeColors.primary.dark} 100%);
  border-radius: ${BorderRadius.lg};
  padding: ${Spacing.xl} ${Spacing.xl};
  margin-bottom: ${Spacing.xl};
  color: white;
  text-align: center;
  position: relative;
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    top: -50%;
    right: -50%;
    width: 200%;
    height: 200%;
    background: radial-gradient(circle, rgba(255,255,255,0.08) 0%, transparent 70%);
  }
`;

const BannerTitle = styled.h1`
  color: white;
  font-family: ${Typography.fontFamily.secondary};
  font-size: 1.8rem;
  font-weight: ${Typography.fontWeight.bold};
  margin: 0 0 ${Spacing.xs} 0;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: ${Spacing.sm};
  position: relative;
  z-index: 1;

  @media (max-width: 768px) {
    font-size: 1.4rem;
  }
`;

const BannerSubtitle = styled.p`
  color: rgba(255,255,255,0.9);
  font-size: 1rem;
  margin: 0;
  position: relative;
  z-index: 1;
`;

const SurveyCard = styled(Card)`
  margin-bottom: ${Spacing.lg};
  padding: ${Spacing.xl};

  @media (max-width: 768px) {
    padding: ${Spacing.md};
  }
`;

const QuestionNumber = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  border-radius: 50%;
  background: ${ThemeColors.primary.main};
  color: white;
  font-size: ${Typography.fontSize.sm};
  font-weight: ${Typography.fontWeight.bold};
  margin-right: ${Spacing.sm};
  flex-shrink: 0;
`;

const QuestionLabel = styled.label`
  display: flex;
  align-items: flex-start;
  font-size: 1rem;
  font-weight: ${Typography.fontWeight.semiBold};
  color: ${ThemeColors.text.primary};
  margin-bottom: ${Spacing.md};
  line-height: 1.5;
`;

const RadioGroup = styled.div`
  display: flex;
  gap: ${Spacing.lg};
  margin-top: ${Spacing.sm};

  @media (max-width: 480px) {
    gap: ${Spacing.md};
  }
`;

const RadioLabel = styled.label<{ selected?: boolean }>`
  display: flex;
  align-items: center;
  gap: ${Spacing.sm};
  padding: ${Spacing.sm} ${Spacing.lg};
  border-radius: ${BorderRadius.md};
  border: 2px solid ${(props) =>
    props.selected ? ThemeColors.primary.main : ThemeColors.border.light};
  background: ${(props) =>
    props.selected ? ThemeColors.primary.main + '12' : ThemeColors.background.paper};
  cursor: pointer;
  transition: all 0.2s ease;
  font-weight: ${Typography.fontWeight.medium};
  color: ${(props) =>
    props.selected ? ThemeColors.primary.dark : ThemeColors.text.primary};
  user-select: none;
  min-width: 80px;
  justify-content: center;

  &:hover {
    border-color: ${ThemeColors.primary.main};
    background: ${ThemeColors.primary.main}0A;
  }

  input {
    display: none;
  }
`;

const DateInput = styled.input`
  padding: ${Spacing.sm} ${Spacing.md};
  border: 1px solid ${ThemeColors.border.light};
  border-radius: ${BorderRadius.md};
  font-family: ${Typography.fontFamily.primary};
  font-size: ${Typography.fontSize.base};
  color: ${ThemeColors.text.primary};
  background-color: ${ThemeColors.background.paper};
  transition: all 0.2s ease;
  width: 100%;
  max-width: 300px;

  &:focus {
    outline: none;
    border-color: ${ThemeColors.primary.main};
    box-shadow: 0 0 0 3px rgba(76, 154, 58, 0.2);
  }
`;

const ConditionalField = styled.div<{ visible: boolean }>`
  max-height: ${(props) => (props.visible ? '300px' : '0')};
  opacity: ${(props) => (props.visible ? 1 : 0)};
  overflow: hidden;
  transition: max-height 0.3s ease, opacity 0.3s ease, margin 0.3s ease;
  margin-top: ${(props) => (props.visible ? Spacing.md : '0')};
`;

const ConditionalLabel = styled.label`
  display: block;
  font-size: ${Typography.fontSize.sm};
  font-weight: ${Typography.fontWeight.medium};
  color: ${ThemeColors.text.secondary};
  margin-bottom: ${Spacing.xs};
`;

const TextAreaStyled = styled.textarea`
  width: 100%;
  min-height: 100px;
  padding: ${Spacing.sm} ${Spacing.md};
  border: 1px solid ${ThemeColors.border.light};
  border-radius: ${BorderRadius.md};
  font-family: ${Typography.fontFamily.primary};
  font-size: ${Typography.fontSize.base};
  color: ${ThemeColors.text.primary};
  background-color: ${ThemeColors.background.paper};
  resize: vertical;
  transition: all 0.2s ease;
  box-sizing: border-box;

  &:focus {
    outline: none;
    border-color: ${ThemeColors.primary.main};
    box-shadow: 0 0 0 3px rgba(76, 154, 58, 0.2);
  }

  &::placeholder {
    color: ${ThemeColors.gray[400]};
  }
`;

const FeedbackTextArea = styled(TextAreaStyled)`
  min-height: 150px;
`;

const SubmitButton = styled(Button)`
  background: linear-gradient(135deg, ${ThemeColors.primary.main} 0%, ${ThemeColors.primary.dark} 100%);
  padding: ${Spacing.md} ${Spacing.xl};
  font-size: 1.1rem;
  font-weight: ${Typography.fontWeight.semiBold};
  box-shadow: 0 4px 12px rgba(76, 154, 58, 0.3);
  display: flex;
  align-items: center;
  gap: ${Spacing.sm};
  width: 100%;
  justify-content: center;
  margin-top: ${Spacing.lg};

  &:hover:not(:disabled) {
    transform: translateY(-2px);
    box-shadow: 0 6px 16px rgba(76, 154, 58, 0.4);
  }

  &:disabled {
    transform: none;
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const SuccessCard = styled(Card)`
  text-align: center;
  padding: ${Spacing.xxl};
  background: linear-gradient(135deg, #f0faf0 0%, #e8f5e9 100%);
  border: 2px solid ${ThemeColors.success.main};
`;

const SuccessIcon = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: ${Spacing.lg};

  svg {
    color: ${ThemeColors.success.main};
  }
`;

const SuccessTitle = styled.h2`
  font-family: ${Typography.fontFamily.secondary};
  font-size: 1.6rem;
  font-weight: ${Typography.fontWeight.bold};
  color: ${ThemeColors.success.dark};
  margin-bottom: ${Spacing.sm};
`;

const SuccessText = styled.p`
  color: ${ThemeColors.text.primary};
  font-size: 1rem;
  margin: 0;
`;

const ReadOnlySummary = styled.div`
  margin-top: ${Spacing.xl};
  text-align: left;
`;

const SummaryItem = styled.div`
  padding: ${Spacing.md};
  border-bottom: 1px solid ${ThemeColors.border.light};
  display: flex;
  flex-direction: column;
  gap: ${Spacing.xs};

  &:last-child {
    border-bottom: none;
  }
`;

const SummaryQuestion = styled.span`
  font-weight: ${Typography.fontWeight.semiBold};
  color: ${ThemeColors.text.primary};
  font-size: ${Typography.fontSize.sm};
`;

const SummaryAnswer = styled.span`
  color: ${ThemeColors.text.secondary};
  font-size: ${Typography.fontSize.base};
`;

const Divider = styled.hr`
  border: none;
  border-top: 1px solid ${ThemeColors.border.light};
  margin: ${Spacing.lg} 0;
`;

const LoadingContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: ${Spacing.xxl};
  gap: ${Spacing.md};
  color: ${ThemeColors.text.secondary};

  svg {
    animation: spin 1s linear infinite;
  }

  @keyframes spin {
    from { transform: rotate(0deg); }
    to { transform: rotate(360deg); }
  }
`;

const RequiredStar = styled.span`
  color: ${ThemeColors.error.main};
  margin-left: 2px;
`;

const NameFieldsRow = styled.div`
  display: flex;
  gap: ${Spacing.md};

  @media (max-width: 480px) {
    flex-direction: column;
  }
`;

const NameInput = styled.input`
  flex: 1;
  padding: ${Spacing.sm} ${Spacing.md};
  border: 1px solid ${ThemeColors.border.light};
  border-radius: ${BorderRadius.md};
  font-family: ${Typography.fontFamily.primary};
  font-size: ${Typography.fontSize.base};
  color: ${ThemeColors.text.primary};
  background-color: ${ThemeColors.background.paper};
  transition: all 0.2s ease;

  &:focus {
    outline: none;
    border-color: ${ThemeColors.primary.main};
    box-shadow: 0 0 0 3px rgba(76, 154, 58, 0.2);
  }

  &::placeholder {
    color: ${ThemeColors.gray[400]};
  }
`;

const PriorSubmissionsList = styled(Card)`
  margin-bottom: ${Spacing.xl};
  padding: ${Spacing.lg};
`;

const PriorSubmissionsTitle = styled.div`
  display: flex;
  align-items: center;
  gap: ${Spacing.sm};
  font-weight: ${Typography.fontWeight.semiBold};
  color: ${ThemeColors.text.primary};
  margin-bottom: ${Spacing.md};
  font-size: ${Typography.fontSize.base};

  svg {
    color: ${ThemeColors.primary.main};
  }
`;

const PriorSubmissionItem = styled.div`
  display: flex;
  align-items: center;
  gap: ${Spacing.sm};
  padding: ${Spacing.sm} ${Spacing.md};
  background: ${ThemeColors.gray[50]};
  border-radius: ${BorderRadius.md};
  margin-bottom: ${Spacing.xs};
  font-size: ${Typography.fontSize.sm};
  color: ${ThemeColors.text.secondary};

  svg {
    color: ${ThemeColors.success.main};
    flex-shrink: 0;
  }
`;

const NewSubmissionButton = styled(Button)`
  margin-top: ${Spacing.md};
  display: flex;
  align-items: center;
  gap: ${Spacing.sm};
  width: 100%;
  justify-content: center;
`;

/* ─────────────────── Component ─────────────────── */

export const EosSurveyScreen: React.FC = () => {
  const { user } = useAuth();

  /* ── State ── */
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [priorSubmissions, setPriorSubmissions] = useState<EosSurveySubmission[]>([]);
  const [showForm, setShowForm] = useState(true);
  const [justSubmitted, setJustSubmitted] = useState(false);

  // Form fields
  const [respondentFirstName, setRespondentFirstName] = useState('');
  const [respondentLastName, setRespondentLastName] = useState('');
  const [lastDayWorked, setLastDayWorked] = useState('');
  const [requireROE, setRequireROE] = useState<boolean | null>(null);
  const [hasContactUpdates, setHasContactUpdates] = useState<boolean | null>(null);
  const [contactUpdates, setContactUpdates] = useState('');
  const [mailedBackEquipment, setMailedBackEquipment] = useState<boolean | null>(null);
  const [reasonNotMailedBack, setReasonNotMailedBack] = useState('');
  const [interestedReturning2027, setInterestedReturning2027] = useState<boolean | null>(null);
  const [wantsTerritoryAdjustments, setWantsTerritoryAdjustments] = useState<boolean | null>(null);
  const [requestedTerritoryChanges, setRequestedTerritoryChanges] = useState('');
  const [feedback, setFeedback] = useState('');

  /* ── Fetch prior submissions for this account ── */
  useEffect(() => {
    if (!user) return;
    const check = async () => {
      try {
        setLoading(true);
        const subs = await EosSurveyService.getSubmissionsByUser(user.id);
        setPriorSubmissions(subs);
      } catch (err) {
        console.error('Error checking existing submissions:', err);
      } finally {
        setLoading(false);
      }
    };
    check();
  }, [user]);

  /* ── Validation ── */
  const isFormValid =
    respondentFirstName.trim() !== '' &&
    respondentLastName.trim() !== '' &&
    lastDayWorked !== '' &&
    requireROE !== null &&
    hasContactUpdates !== null &&
    mailedBackEquipment !== null &&
    interestedReturning2027 !== null &&
    wantsTerritoryAdjustments !== null &&
    // Conditional validations
    (hasContactUpdates === false || contactUpdates.trim() !== '') &&
    (mailedBackEquipment === true || reasonNotMailedBack.trim() !== '') &&
    (wantsTerritoryAdjustments === false || requestedTerritoryChanges.trim() !== '');

  /* ── Submit ── */
  const handleSubmit = async () => {
    if (!user || !isFormValid) return;

    try {
      setSubmitting(true);

      const displayName =
        user.firstName
          ? `${user.firstName} ${user.lastName || ''}`.trim()
          : user.username || user.email.split('@')[0];

      const data: Omit<EosSurveySubmission, 'id' | 'submittedAt'> = {
        submittedBy: user.id,
        submittedByName: displayName,
        submittedByEmail: user.email,
        respondentFirstName: respondentFirstName.trim(),
        respondentLastName: respondentLastName.trim(),
        lastDayWorked,
        requireROE: requireROE!,
        hasContactUpdates: hasContactUpdates!,
        ...(hasContactUpdates ? { contactUpdates: contactUpdates.trim() } : {}),
        mailedBackEquipment: mailedBackEquipment!,
        ...(mailedBackEquipment === false
          ? { reasonNotMailedBack: reasonNotMailedBack.trim() }
          : {}),
        interestedReturning2027: interestedReturning2027!,
        wantsTerritoryAdjustments: wantsTerritoryAdjustments!,
        ...(wantsTerritoryAdjustments
          ? { requestedTerritoryChanges: requestedTerritoryChanges.trim() }
          : {}),
        feedback: feedback.trim(),
      };

      await EosSurveyService.submitSurvey(data);

      // Refetch and show success
      const subs = await EosSurveyService.getSubmissionsByUser(user.id);
      setPriorSubmissions(subs);
      setJustSubmitted(true);
      setShowForm(false);

      toast.success('Survey submitted successfully!');
    } catch (err: any) {
      console.error('Submit error:', err);
      toast.error(err.message || 'Failed to submit survey');
    } finally {
      setSubmitting(false);
    }
  };

  /* ── Helpers ── */
  const formatDate = (val: any): string => {
    if (!val) return 'N/A';
    if (typeof val === 'string') {
      // ISO string from date input
      const d = new Date(val + 'T00:00:00');
      return d.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
      });
    }
    const d = val.toDate ? val.toDate() : new Date(val);
    return d.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  /* ── Reset form for new submission ── */
  const resetForm = () => {
    setRespondentFirstName('');
    setRespondentLastName('');
    setLastDayWorked('');
    setRequireROE(null);
    setHasContactUpdates(null);
    setContactUpdates('');
    setMailedBackEquipment(null);
    setReasonNotMailedBack('');
    setInterestedReturning2027(null);
    setWantsTerritoryAdjustments(null);
    setRequestedTerritoryChanges('');
    setFeedback('');
    setJustSubmitted(false);
    setShowForm(true);
  };

  /* ── Loading state ── */
  if (loading) {
    return (
      <SurveyPageContainer>
        <LoadingContainer>
          <Loader2 size={32} />
          <span>Loading survey…</span>
        </LoadingContainer>
      </SurveyPageContainer>
    );
  }

  /* ── Just submitted: success view ── */
  if (justSubmitted && !showForm) {
    return (
      <SurveyPageContainer>
        <Toaster position="top-center" />
        <SurveyBanner>
          <BannerTitle>
            <ClipboardList size={28} />
            EOS Survey 2026
          </BannerTitle>
          <BannerSubtitle>End of Season Survey</BannerSubtitle>
        </SurveyBanner>

        <SuccessCard>
          <SuccessIcon>
            <CheckCircle size={56} />
          </SuccessIcon>
          <SuccessTitle>Survey Submitted Successfully!</SuccessTitle>
          <SuccessText>
            Thank you for completing the EOS Survey.
          </SuccessText>
        </SuccessCard>

        {/* Show who has submitted from this account */}
        {priorSubmissions.length > 0 && (
          <PriorSubmissionsList>
            <PriorSubmissionsTitle>
              <Users size={18} />
              Submissions from this account ({priorSubmissions.length})
            </PriorSubmissionsTitle>
            {priorSubmissions.map((s) => (
              <PriorSubmissionItem key={s.id}>
                <CheckCircle size={16} />
                {s.respondentFirstName} {s.respondentLastName} — submitted {formatDate(s.submittedAt)}
              </PriorSubmissionItem>
            ))}
          </PriorSubmissionsList>
        )}

        <NewSubmissionButton onClick={resetForm}>
          <ClipboardList size={18} />
          Submit Another Survey (different person)
        </NewSubmissionButton>

        <Spacer height={Spacing.xxl} />
      </SurveyPageContainer>
    );
  }

  /* ── Survey form ── */
  return (
    <SurveyPageContainer>
      <Toaster position="top-center" />

      <SurveyBanner>
        <BannerTitle>
          <ClipboardList size={28} />
          EOS Survey 2026
        </BannerTitle>
        <BannerSubtitle>
          Please complete the following End of Season survey
        </BannerSubtitle>
      </SurveyBanner>

      {/* Show prior submissions from this account (names only) */}
      {priorSubmissions.length > 0 && (
        <PriorSubmissionsList>
          <PriorSubmissionsTitle>
            <Users size={18} />
            Already submitted from this account ({priorSubmissions.length})
          </PriorSubmissionsTitle>
          {priorSubmissions.map((s) => (
            <PriorSubmissionItem key={s.id}>
              <CheckCircle size={16} />
              {s.respondentFirstName} {s.respondentLastName}
            </PriorSubmissionItem>
          ))}
        </PriorSubmissionsList>
      )}

      {/* Name — First question */}
      <SurveyCard>
        <QuestionLabel>
          <QuestionNumber><User size={14} /></QuestionNumber>
          Name (First / Last)<RequiredStar>*</RequiredStar>
        </QuestionLabel>
        <NameFieldsRow>
          <NameInput
            type="text"
            value={respondentFirstName}
            onChange={(e) => setRespondentFirstName(e.target.value)}
            placeholder="First name"
            id="eos-first-name"
          />
          <NameInput
            type="text"
            value={respondentLastName}
            onChange={(e) => setRespondentLastName(e.target.value)}
            placeholder="Last name"
            id="eos-last-name"
          />
        </NameFieldsRow>
      </SurveyCard>

      {/* Q1 — Last Day Worked */}
      <SurveyCard>
        <QuestionLabel>
          <QuestionNumber>1</QuestionNumber>
          What was your last day worked?<RequiredStar>*</RequiredStar>
        </QuestionLabel>
        <DateInput
          type="date"
          value={lastDayWorked}
          onChange={(e) => setLastDayWorked(e.target.value)}
          max={new Date().toISOString().split('T')[0]}
          id="eos-last-day-worked"
        />
      </SurveyCard>

      {/* Q2 — ROE */}
      <SurveyCard>
        <QuestionLabel>
          <QuestionNumber>2</QuestionNumber>
          Do you require we issue an ROE?<RequiredStar>*</RequiredStar>
        </QuestionLabel>
        <RadioGroup>
          <RadioLabel selected={requireROE === true}>
            <input
              type="radio"
              name="requireROE"
              checked={requireROE === true}
              onChange={() => setRequireROE(true)}
            />
            Yes
          </RadioLabel>
          <RadioLabel selected={requireROE === false}>
            <input
              type="radio"
              name="requireROE"
              checked={requireROE === false}
              onChange={() => setRequireROE(false)}
            />
            No
          </RadioLabel>
        </RadioGroup>
      </SurveyCard>

      {/* Q3 — Contact Info */}
      <SurveyCard>
        <QuestionLabel>
          <QuestionNumber>3</QuestionNumber>
          Do you have any contact information updates (mailing address/phone
          number)?<RequiredStar>*</RequiredStar>
        </QuestionLabel>
        <RadioGroup>
          <RadioLabel selected={hasContactUpdates === true}>
            <input
              type="radio"
              name="contactUpdates"
              checked={hasContactUpdates === true}
              onChange={() => setHasContactUpdates(true)}
            />
            Yes
          </RadioLabel>
          <RadioLabel selected={hasContactUpdates === false}>
            <input
              type="radio"
              name="contactUpdates"
              checked={hasContactUpdates === false}
              onChange={() => {
                setHasContactUpdates(false);
                setContactUpdates('');
              }}
            />
            No
          </RadioLabel>
        </RadioGroup>
        <ConditionalField visible={hasContactUpdates === true}>
          <ConditionalLabel htmlFor="eos-contact-updates">
            Please provide your updated information
          </ConditionalLabel>
          <TextAreaStyled
            id="eos-contact-updates"
            value={contactUpdates}
            onChange={(e) => setContactUpdates(e.target.value)}
            placeholder="Enter updated mailing address, phone number, etc."
          />
        </ConditionalField>
      </SurveyCard>

      {/* Q4 — Equipment Mailed Back */}
      <SurveyCard>
        <QuestionLabel>
          <QuestionNumber>4</QuestionNumber>
          Did you mail back the EOS equipment/paperwork?<RequiredStar>*</RequiredStar>
        </QuestionLabel>
        <RadioGroup>
          <RadioLabel selected={mailedBackEquipment === true}>
            <input
              type="radio"
              name="mailedBack"
              checked={mailedBackEquipment === true}
              onChange={() => {
                setMailedBackEquipment(true);
                setReasonNotMailedBack('');
              }}
            />
            Yes
          </RadioLabel>
          <RadioLabel selected={mailedBackEquipment === false}>
            <input
              type="radio"
              name="mailedBack"
              checked={mailedBackEquipment === false}
              onChange={() => setMailedBackEquipment(false)}
            />
            No
          </RadioLabel>
        </RadioGroup>
        <ConditionalField visible={mailedBackEquipment === false}>
          <ConditionalLabel htmlFor="eos-reason-not-mailed">
            Reason not mailed back
          </ConditionalLabel>
          <TextAreaStyled
            id="eos-reason-not-mailed"
            value={reasonNotMailedBack}
            onChange={(e) => setReasonNotMailedBack(e.target.value)}
            placeholder="Please explain why the equipment/paperwork has not been mailed back"
          />
        </ConditionalField>
      </SurveyCard>

      {/* Q5 — Returning 2027 */}
      <SurveyCard>
        <QuestionLabel>
          <QuestionNumber>5</QuestionNumber>
          Are you interested in returning for the 2027 season (beginning December
          2026)?<RequiredStar>*</RequiredStar>
        </QuestionLabel>
        <RadioGroup>
          <RadioLabel selected={interestedReturning2027 === true}>
            <input
              type="radio"
              name="returning"
              checked={interestedReturning2027 === true}
              onChange={() => setInterestedReturning2027(true)}
            />
            Yes
          </RadioLabel>
          <RadioLabel selected={interestedReturning2027 === false}>
            <input
              type="radio"
              name="returning"
              checked={interestedReturning2027 === false}
              onChange={() => setInterestedReturning2027(false)}
            />
            No
          </RadioLabel>
        </RadioGroup>
      </SurveyCard>

      {/* Q6 — Territory Adjustments */}
      <SurveyCard>
        <QuestionLabel>
          <QuestionNumber>6</QuestionNumber>
          Would you like to see any adjustments to your territory?<RequiredStar>*</RequiredStar>
        </QuestionLabel>
        <RadioGroup>
          <RadioLabel selected={wantsTerritoryAdjustments === true}>
            <input
              type="radio"
              name="territory"
              checked={wantsTerritoryAdjustments === true}
              onChange={() => setWantsTerritoryAdjustments(true)}
            />
            Yes
          </RadioLabel>
          <RadioLabel selected={wantsTerritoryAdjustments === false}>
            <input
              type="radio"
              name="territory"
              checked={wantsTerritoryAdjustments === false}
              onChange={() => {
                setWantsTerritoryAdjustments(false);
                setRequestedTerritoryChanges('');
              }}
            />
            No
          </RadioLabel>
        </RadioGroup>
        <ConditionalField visible={wantsTerritoryAdjustments === true}>
          <ConditionalLabel htmlFor="eos-territory-changes">
            Requested changes
          </ConditionalLabel>
          <TextAreaStyled
            id="eos-territory-changes"
            value={requestedTerritoryChanges}
            onChange={(e) => setRequestedTerritoryChanges(e.target.value)}
            placeholder="Describe the territory adjustments you'd like to see"
          />
        </ConditionalField>
      </SurveyCard>

      {/* Q7 — Feedback */}
      <SurveyCard>
        <QuestionLabel>
          <QuestionNumber>7</QuestionNumber>
          Feedback from the 2026 season / Improvements Needed / Suggestions /
          Helpful Tools
        </QuestionLabel>
        <FeedbackTextArea
          id="eos-feedback"
          value={feedback}
          onChange={(e) => setFeedback(e.target.value)}
          placeholder="Share your feedback, suggestions, or ideas for improvement…"
        />
      </SurveyCard>

      {/* Submit */}
      <SubmitButton
        onClick={handleSubmit}
        disabled={!isFormValid || submitting}
        id="eos-submit-btn"
      >
        {submitting ? (
          <>
            <Loader2 size={20} className="spin" />
            Submitting…
          </>
        ) : (
          <>
            <Send size={20} />
            Submit Survey
          </>
        )}
      </SubmitButton>

      <Spacer height={Spacing.xxl} />
    </SurveyPageContainer>
  );
};

export default EosSurveyScreen;
